import { collection, addDoc, updateDoc, deleteDoc, doc, getDocs, query, orderBy, limit, DocumentData, getDoc, Timestamp, serverTimestamp, FieldValue } from 'firebase/firestore';
import { db } from './firebase';
import { tmdbApi } from './tmdb';

import { store } from '../store';
import { BlogPost, Translation } from '../types/blog';
import { Language } from '../types';

const COLLECTION_NAME = 'blog_posts';

const convertTimestampToDate = (timestamp: Timestamp | Date | string | null | undefined) => {
  if (!timestamp) {
    return new Date().toISOString();
  }

  // Type-safe check for Firestore Timestamp
  if (typeof timestamp === 'object' && timestamp !== null && 'toDate' in timestamp && typeof (timestamp as any).toDate === 'function') {
    return (timestamp as Timestamp).toDate().toISOString();
  }

  if (timestamp instanceof Date) {
    return timestamp.toISOString();
  }

  if (typeof timestamp === 'string') {
    return timestamp;
  }

  return new Date().toISOString();
};

const getTMDBDetails = async (type: 'film' | 'serie', title: string, year: string) => {
  try {
    const searchType = type === 'film' ? 'movie' : 'tv';
    const response = await tmdbApi.search(title);
    const results = response.results.filter(item => item.media_type === searchType);
    if (results && results.length > 0) {
      const matchingResults = year
        ? results.filter(item => {
            const itemYear = new Date(
              item.release_date || item.first_air_date
            ).getFullYear().toString();
            return itemYear === year;
          })
        : results;

      if (matchingResults.length > 0) {
        const item = matchingResults[0];
        return {
          tmdbId: item.id,
          image: item.backdrop_path
            ? `https://image.tmdb.org/t/p/original${item.backdrop_path}`
            : undefined,
          poster: item.poster_path
            ? `https://image.tmdb.org/t/p/original${item.poster_path}`
            : undefined,
        };
      }
    }
    return null;
  } catch (error) {
    console.error('Error fetching TMDB details:', error);
    return null;
  }
};

const blogService = {
  async getAllPosts(): Promise<BlogPost[]> {
    try {
      const q = query(
        collection(db, COLLECTION_NAME),
        orderBy('createdAt', 'desc')
      );
      const querySnapshot = await getDocs(q);
      const posts = querySnapshot.docs.map(doc => ({
        ...doc.data(),
        id: doc.id,
        createdAt: convertTimestampToDate(doc.data().createdAt),
        updatedAt: convertTimestampToDate(doc.data().updatedAt),
      })) as BlogPost[];

      return posts;
    } catch (error) {
      console.error('Error getting blog posts:', error);
      throw error;
    }
  },

  async createPost(post: {
    title: string;
    content: string;
    type: 'film' | 'serie';
    tmdbId: number;
    year: string;
    image: string;
    poster: string;
    overview: string;
    rating: number;
    tags: string[];
    authorId: string;
  }): Promise<string> {
    try {
      const newPost = {
        ...post,
        originalLanguage: Language.FR,
        translations: {},
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      const docRef = await addDoc(collection(db, COLLECTION_NAME), newPost);
      return docRef.id;
    } catch (error) {
      console.error('Error creating blog post:', error);
      throw error;
    }
  },

  async getPostById(id: string): Promise<BlogPost> {
    try {
      const docRef = doc(db, COLLECTION_NAME, id);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        throw new Error('Blog post not found');
      }

      const data = docSnap.data();
      const post = {
        ...data,
        id: docSnap.id,
        createdAt: convertTimestampToDate(data.createdAt),
        updatedAt: convertTimestampToDate(data.updatedAt),
      } as BlogPost;

      return post;
    } catch (error) {
      console.error('Error getting blog post:', error);
      throw error;
    }
  },

  async updatePost(id: string, post: Partial<Omit<BlogPost, 'id'>>): Promise<void> {
    try {
      const docRef = doc(db, COLLECTION_NAME, id);
      const updateData = {
        ...post,
        updatedAt: serverTimestamp(),
      };

      // Si le contenu est modifié, mettre à jour la langue originale et réinitialiser les traductions
      if (post.title || post.content || post.overview) {
        const currentPost = await this.getPostById(id);
        updateData.originalLanguage = currentPost.originalLanguage || Language.FR;
        
        // Réinitialiser les traductions car le contenu a changé
        updateData.translations = {};
      }

      await updateDoc(docRef, updateData);
    } catch (error) {
      console.error('Error updating blog post:', error);
      throw error;
    }
  },

  async deletePost(id: string): Promise<void> {
    try {
      const docRef = doc(db, COLLECTION_NAME, id);
      await deleteDoc(docRef);
    } catch (error) {
      console.error('Error deleting blog post:', error);
      throw error;
    }
  },

  async translatePost(post: BlogPost, targetLang: Language): Promise<BlogPost> {
    try {
      // Détecter la langue source (on suppose que le titre est dans la même langue que le contenu)
      const sourceLang = post.originalLanguage || Language.FR; // Par défaut, on considère que le contenu est en français

      // Si la langue cible est la même que la langue source, retourner le contenu original
      if (targetLang === sourceLang) {
        return post;
      }

      console.log('Original content:', post.content.substring(0, 100));
      console.log('Source language:', sourceLang);
      console.log('Target language:', targetLang);

      // Créer un objet de traduction vide si nécessaire
      if (!post.translations) {
        post.translations = {};
      }

      // Traduire uniquement si la traduction n'existe pas déjà
      if (!post.translations[targetLang]) {
        const [translatedTitle, translatedContent, translatedOverview] = await Promise.all([
          Promise.resolve(post.title),
          Promise.resolve(post.content),
          Promise.resolve(post.overview ?? '')
        ]);

        console.log('Translated content:', translatedContent.substring(0, 100));

        // Créer l'objet de traduction
        const translation: Translation = {
          title: translatedTitle,
          content: translatedContent,
          overview: translatedOverview
        };

        // Mettre à jour le post dans Firebase avec les traductions
        const postRef = doc(db, COLLECTION_NAME, post.id);
        const translations = {
          ...post.translations,
          [targetLang]: translation
        };

        await updateDoc(postRef, {
          translations,
          originalLanguage: sourceLang
        });

        return {
          ...post,
          translations,
          originalLanguage: sourceLang
        };
      }

      return post;
    } catch (error) {
      console.error('Error translating post:', error);
      throw error;
    }
  }
};

export default blogService;
